  <template>
    <v-row class="mb-1">
      <v-col cols="3" align-self="auto">
        <v-select :items="availableAggregationLevels"
                  v-model="selectedTableAggregationLevel"
                  density="compact"
                  variant="outlined"
                  label="Aggregation Level:"
        />
      </v-col>
      <v-spacer/>
      <v-col cols="auto" id="download-original">
        <DownloadSpiAndSiiTablesButton :sii-table-data="tableData"
                                       :spi-table-data="buttonData"
                                       :project-id="projectId"
                                       :project-name="projectName"
                                       :user-email="userEmail"
        />
      </v-col>
    </v-row>

    <v-table density="comfortable" style="background: transparent" class="scrollable-horizontal">
      <thead :class="tableGradient">
      <tr>
        <th class="text-center rounded-top-left-border" rowspan="2">KPI</th>
        <th class="text-center" rowspan="2">Functional Unit</th>
        <th class="text-center" rowspan="2">Assigned Weight</th>
        <th class="text-center" colspan="5">Evaluation Scale Indicative Margins</th>
        <th class="text-center" rowspan="2">FU Input</th>
        <th class="text-center" rowspan="2">Score</th>
        <th class="text-center rounded-top-right-border" rowspan="2">Weighted Score</th>
      </tr>
      <tr>
        <th class="text-center" >(1)</th>
        <th class="text-center" >(2)</th>
        <th class="text-center" >(3)</th>
        <th class="text-center" >(4)</th>
        <th class="text-center" >(5)</th>
      </tr>
      </thead>

      <tbody>
      <template v-for="(kpi, index) in filteredTableData" :key="index">

        <tr class="bg-white"  v-if="index === 0 || filteredTableData[index-1].dimension_name !== kpi.dimension_name">
          <td colspan="11" class="text-center font-weight-black">
            {{ StringManipulation.methods.clearAndCapitalize(kpi.dimension_name) }} Dimension
          </td>
        </tr>

        <tr :class="index % 2 === 0 ? evenBackgroundRowColorCoding[selectedTableAggregationLevel] : oddBackgroundRowColorCoding[selectedTableAggregationLevel]">
          <td class="py-3 pl-5 kpi-text"><b>{{ index + 1 }}.  {{ StringManipulation.methods.clearAndCapitalize(kpi.name) }}</b></td>
          <td class="text-center"> {{ kpi.functional_unit === null ? '-' : kpi.functional_unit.functional_unit }} </td>
          <td class="text-center"> {{ parseFloat(kpi.assigned_weight).toFixed(3) }} </td>
          <template v-if="kpi.m1 === null && isLikertScale(kpi.functional_unit)">
            <td class="text-center"> 1 </td>
            <td class="text-center"> 2 </td>
            <td class="text-center"> 3 </td>
            <td class="text-center"> 4 </td>
            <td class="text-center"> 5 </td>
          </template>
          <template v-else-if="kpi.m1 === null">
            <td class="text-center text-grey" colspan="5"> <i>No margins provided.</i> </td>
          </template>
          <template v-else>
            <td class="text-center"> {{ getFirstMargin(kpi.m0, kpi.m3) }} </td>
            <td class="text-center"> {{ getMarginsBetween(kpi.m0, kpi.m1, false) }} </td>
            <td class="text-center"> {{ getMarginsBetween(kpi.m1, kpi.m2, true) }} </td>
            <td class="text-center"> {{ getMarginsBetween(kpi.m2, kpi.m3, false) }} </td>
            <td class="text-center"> {{ getLastMargin(kpi.m3, kpi.m0) }} </td>
          </template>
          <td class="text-center"> {{ beautifyAndRoundNumber(kpi.functional_unit_input) }} </td>
          <td class="text-center"> {{ kpi.kpi_score }} </td>
          <td class="text-center"> {{ (kpi.kpi_score * kpi.assigned_weight).toFixed(3) }} </td>
        </tr>

        <tr class="bg-white" v-if="index === filteredTableData.length - 1 || filteredTableData[index+1].dimension_name !== kpi.dimension_name">
          <td colspan="10">
            Final SII Score of <b> {{ StringManipulation.methods.clearAndCapitalize(kpi.dimension_name) }}</b> Dimension:
          </td>
          <td class="text-center font-weight-bold">
            {{ kpi.dimension_score.toString().includes('.') ? parseFloat(kpi.dimension_score).toFixed(3) : kpi.dimension_score}}
          </td>
        </tr>

      </template>
      </tbody>
    </v-table>
  </template>

  <script>

  /**
   * Takes a number that is usually a float, and it returns it with beautification.
   * @param number The number to be processed.
   * @returns {string|number|*} The number as it is, if it's less than 1,000, and it doesn't end with .0 (e.g. 44.0).
   * Removes the .0 and adds comma separators, otherwise.
   */
  export const beautifyAndRoundNumber = (number) => {
    let beautifiedNumber = number.toString().endsWith('.0') ? Math.round(number) : number;
    return parseFloat(beautifiedNumber) > 1_000 ? beautifiedNumber.toLocaleString('en-US') : beautifiedNumber.toString();
  };

  /**
   * Determines what the first margin should look like on the table. Practically adds the correct math symbol.
   * @param first The first margin of all margins.
   * @param last The last (fourth) margin of all margins.
   * @returns {string} A string that is ≥[margin] if the first margin is greater than the last, ≤[margin] otherwise
   */
  export const getFirstMargin = (first, last) => {
    if (first === null || last === null)
      return '-';

    const symbol = parseFloat(first) > parseFloat(last) ? '≥' : '≤';
    const margin = beautifyAndRoundNumber(first);

    return `${symbol}${margin}`;
  };

  /**
   * Puts the margins in the correct mathematical range order.
   * @param first The first appearing margin
   * @param last The last appearing margin
   * @param inclusive Determines the brackets to be used. If they're inclusive, the [] brackets will be used. If they're not, the () brackets will be used.
   * @returns {string} A string that is mathematical range putting the smallest number first, and the bigger one to the end. [small, big] or (small, big), determined by the settings.
   */
  export const getMarginsBetween = (first, last, inclusive) => {
    if (first === null || last === null)
      return '-';

    let margin1 = parseFloat(first) > parseFloat(last) ? last : first;
    let margin2 = parseFloat(first) > parseFloat(last) ? first : last;

    margin1 = beautifyAndRoundNumber(margin1);
    margin2 = beautifyAndRoundNumber(margin2);

    return inclusive? `[${margin1}, ${margin2}]` : `(${margin1}, ${margin2})`;
  };

  /**
   * Determines what the last margin should look like on the table. Practically adds the correct math symbol. (Just like
   * `getFirstMargin`).
   * @param first The first margin of all margins.
   * @param last The last (fourth) margin of all margins.
   * @returns {string} A string that is ≥[margin] if the last margin is greater than the first, ≤[margin] otherwise
   */
  export const getLastMargin = (last, first) => {
    if (first === null || last === null)
      return '-';

    const symbol = parseFloat(first) < parseFloat(last) ? '≥' : '≤';
    const margin = beautifyAndRoundNumber(last);

    return `${symbol}${margin}`;
  };

  /**
   * Null-safe check for Likert-Scale. Used in the templates to check a special null-case in the margins.
   * @param functional_unit The functional unit object provided by the JSON.
   * @returns {boolean} True if the functional unit name contains the word 'Likert', false otherwise.
   */
  export const isLikertScale = (functional_unit) => {
    if (functional_unit === null)
      return false;

    return functional_unit.functional_unit.toString().includes('Likert');
  }
  </script>

  <script setup>
  import StringManipulation from "@/Mixins/StringManipulation";
  import {computed, onMounted, ref} from "vue";
  import DownloadSpiAndSiiTablesButton from "@/Pages/Projects/subcomponents/DownloadSpiAndSiiTablesButton.vue";

  const props = defineProps({
    tableData: {
      type: Object,
      required: true,
    },
    buttonData: {
      type: Object,
      required: true,
    },
    projectName: {
      type: String,
      required: true,
    },
    projectId: {
      type: Number,
      required: true
    },
    userEmail: {
      type: String,
      required: true,
    },
  });

  onMounted(() => {
    availableAggregationLevels.value = Object.keys(props.tableData);
    selectedTableAggregationLevel.value = availableAggregationLevels.value[0];
  });

  const selectedTableAggregationLevel = ref('');
  const availableAggregationLevels = ref([]);

  const tableGradient = computed(() => {
    return tableGradientClassColorCoding[selectedTableAggregationLevel.value];
  });

  const filteredTableData = computed(() => {
    return props.tableData[selectedTableAggregationLevel.value];
  });

  const tableGradientClassColorCoding = {
    'Building' : 'building-gradient',
    'PEB': 'peb-gradient',
    'PED': 'ped-gradient',
    'City': 'city-gradient',
    'Island': 'island-gradient',
  };
  const evenBackgroundRowColorCoding = {
    'Building' : 'building-even-row',
    'PEB': 'peb-even-row',
    'PED': 'ped-even-row',
    'City': 'city-even-row',
    'Island': 'island-even-row',
  };
  const oddBackgroundRowColorCoding = {
    'Building' : 'building-odd-row',
    'PEB': 'peb-odd-row',
    'PED': 'ped-odd-row',
    'City': 'city-odd-row',
    'Island': 'island-odd-row',
  };

  </script>

  <style scoped>
  /* Do NOT erase these gradients. They appear as unused, but they're being used dynamically by JS! */
  .peb-gradient {
    background: linear-gradient(to left, #1f469c, #5487d0) !important;
    color: white;
  }

  .ped-gradient {
    background: linear-gradient(to left, #255e12, #4b913a) !important;
    color: white;
  }

  .city-gradient {
    background: linear-gradient(to left, #c87000, #edab00) !important;
    color: white;
  }

  .building-gradient {
    background: linear-gradient(to left, #9c1f1f, #d05454) !important;
    color: white;
  }

  .island-gradient {
    background: linear-gradient(to left, #601f9c, #9054d0) !important;
    color: white;
  }

  .building-even-row {
    background: #fff1f1;
  }

  .building-odd-row {
    background: #ffdede;
  }

  .ped-even-row {
    background: #f7fff1;
  }

  .ped-odd-row {
    background: #daf4d8;
  }

  .peb-even-row {
    background: #f4f8ff;
  }

  .peb-odd-row {
    background: #e4eaff;
  }

  .city-even-row {
    background: #fff8f2;
  }

  .city-odd-row {
    background: #ffeddd;
  }

  .island-even-row {
    background: #f6f2ff;
  }

  .island-odd-row {
    background: #e9ddff;
  }

  .rounded-top-left-border {
    border-top-left-radius: 30px;
    background-color: transparent !important;
  }

  .rounded-top-right-border {
    border-top-right-radius: 30px;
    border-color: #a6a6a6 !important;
  }

  .scrollable-horizontal {
    overflow-y: auto;
  }

  .kpi-text {
    max-lines: 2;
    max-width: 50rch;

  }
  </style>